import { useRef, useState, useEffect } from "react";
import { DialogContent, Button, Divider, Grid, Paper, TextField, Tooltip, IconButton } from "@mui/material";
import { RemoveCircleOutline, Save, SwapHorizOutlined } from "@mui/icons-material";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { updateActiveTask, Util } from "../../util/Util";
import { mapTasksWithActivation, refreshEmptyActiveTaskDate } from "../../util/taskActivation";
import { loadPendingTasks, persistPendingTasks, revivePendingItems, useSkipFirstRun } from "../../util/pendingTaskStorage";
import { usePendingWorkSessions } from "../../hooks/usePendingWorkSessions";
import { flushItemTableItems } from "../../util/itemTableFlush";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Item } from "../../util/Item";
import MoveDetail from "./MoveDetail";
import TaskPane from "../../components/TaskPane";
import MoveItemDetail from "./MoveItemDetail";
import ItemTable from "../../components/ItemTable";

import { useAuth } from "../../hooks/useAuth";
import { useDepotAccess } from "../../hooks/useDepotAccess";

import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { useMove } from "./MoveContext";
import { MovementTask } from './MoveContext';
import { DepotEnum, LocalStorageKeys, MYSQL_LIMITS, OperationEnum } from "../../util/constants";
import { useAppSocket } from "../../providers/AppSocketContext";
import { v4 } from "uuid";

import { addMoney } from "../../util/quantity";
import { useEphemeralEditTask } from "../../hooks/useEphemeralEditTask";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle?: any;
	onSuccess?: () => void;
}

const MovementModal = ({ isOpen, toggle, bundle, onSuccess }: Props) => {
	const [taskHash, setTaskHash] = useState<string | null>(null);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [renderChildren, setRenderChildren] = useState(false);
	const [tasksOpen, setTasksOpen] = useState(false);
	const [validateDetail, setValidateDetail] = useState(false);

	const { api, session, access } = useAuth();
	const { ensureRoute } = useDepotAccess();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { pendingTasks, setPendingTasks, fetchedTask, setFetchedTask, activeTask, addNewTask, setItems, setIsModalOpen, items, apiRef } = useMove();

	// An existing document's draft is scratch work: it must not survive the
	// modal, saved or not, nor reach storage with the real drafts.
	useEphemeralEditTask({
		editing: Boolean(bundle),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
	});
	const { appSettings } = useAppSocket();
	const themeColor = useMoleThemeColor();
	const currency = appSettings?.BIZ_CURRENCY?.value || "USD";

	const prevTaskIdRef = useRef<string | null>(null);
	const validateBtnRef = useRef<HTMLButtonElement>(null);
	const changeRef = useRef<boolean>(false);
	const skipFirstPersist = useSkipFirstRun();

	const reviveMovementTask = (task: any): MovementTask<Item> =>
		refreshEmptyActiveTaskDate({
			...task,
			createdAt: new Date(task.createdAt),
			updatedAt: new Date(task.updatedAt),
			items: revivePendingItems(task.items, (item: any) => new Item(item)),
			is_public: Boolean(task.is_public),
			server_v: Number(task.server_v || 0),
			total: function () {
				return (this.items ?? []).reduce((sum: number, item: { total: number }) => addMoney(sum, item.total), 0);
			},
		});

	const { userId, removeRemote, setPublic } = usePendingWorkSessions({
		workType: "transfer",
		enabled: Boolean(isOpen && !bundle),
		tasks: pendingTasks,
		setTasks: setPendingTasks,
		reviveTask: reviveMovementTask,
		storageKey: LocalStorageKeys.MOVEMENT_TASKS,
	});

	const { createdAt, updatedAt, active, items: itms, ...bundleDetails } = activeTask || {};

	const bundleItems = itms
		? Object.values(itms).map(({ stock, ...rest }) => rest)
		: [];

	const canValidate = activeTask && Object.keys(activeTask.items!).length > 0
		&& validateDetail
		&& activeTask.date // TODO: validate this 
		&& (bundle ? taskHash !== Util.hashObject(JSON.stringify(bundleDetails) + JSON.stringify(bundleItems)) : true);

	const fetchData = async () => {
		setLoadingMessage("FETCH");

		const res = await api.get(`/records/${bundle.batch}`);

		if (res) {
			if (res.status === 200) {
				res.data.records.sort((a: any, b: any) => a.item_name.localeCompare(b.item_name));
				setFetchedTask(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setLoadingMessage(null);
	};

	const onSubmit = async () => {
		setConfirmModalOpen(true);
	};

	const handleConfirm = async () => {
		if (!activeTask) return;

		if (!Util.checkDating(activeTask.date, access, 'movement', bundle ? 'update' : 'create')) {
			return;
		}

		if (!ensureRoute(activeTask.operation, activeTask.source, activeTask.destination)) {
			return;
		}

		const batch = bundle ? activeTask.id : v4();

		const details = {
			batch: batch,
			operation: activeTask.operation,
			receiver: activeTask.receiver,
			source_depot: activeTask.source,
			dest_depot: activeTask.destination,
			rec_date: activeTask.date,
			ref: activeTask.reference,
			description: activeTask.description
		};

		const taskItems = flushItemTableItems<Item>(apiRef) ?? activeTask.items!;
		const records = taskItems.map(({ id, item, price, quantity }) => ({ item: id, item_desc: item, amount: price, quantity, batch: batch }));

		const payload = {
			details: details,
			records: records
		}

		setLoadingMessage(bundle ? "UPDATE" : "SAVE");

		const res = !bundle ? await api.post("/movements", payload) : await api.put(`/movements/${batch}`, payload);

		if (res) {
			if (res.status === 201 || res.status === 204) {
				if (bundle) {
					toggle(); // Close modal
				} else {
					toast.success(t("common:toast.success.transfer"), { autoClose: 3000 });
					deleteTask(activeTask.id, { posted: true }); // Posted: clear it for everyone, not just its owner
				}
				changeRef.current = true;
			} else if (res.status === 409) {
				toast.error(t("common:toast.error.integrity"), { autoClose: 3000 });
			} else {
				console.error("Failed to send data:", res);
			}
			// TODO: handle 400 bad requests
		}

		setLoadingMessage(null);
	}

	const deleteTask = (taskId: string, { posted = false }: { posted?: boolean } = {}) => {
		const existing = pendingTasks.find((task) => task.id === taskId);
		setPendingTasks((prevTasks) => prevTasks.filter((task) => task.id !== taskId));
		void removeRemote(taskId, existing?.server_v, { posted });
	};

	useEffect(() => {
		setIsModalOpen(isOpen);

		if (isOpen) {
			if (bundle) {
				addNewTask();
			} else {
				const parsedTasks = loadPendingTasks(LocalStorageKeys.MOVEMENT_TASKS, reviveMovementTask, session?.user_id);
				if (parsedTasks) {
					setPendingTasks(parsedTasks);
					return;
				}
				addNewTask();
			}
		}
	}, [isOpen]);

	useEffect(() => {
		if (isOpen && !bundle) {
			const handleKeyDown = (e: KeyboardEvent) => {
				// Ctrl+Alt+Q — task pane (avoid bare Ctrl+Q: quits many browsers)
				if (!bundle && e.ctrlKey && e.altKey && e.key.toLowerCase() === "q") {
					e.preventDefault();
					setTasksOpen((open) => !open);
				} else if (e.ctrlKey && e.altKey && e.key.toLowerCase() === "s") {
					e.preventDefault();
					validateBtnRef.current?.click();
				}
			};

			document.addEventListener("keydown", handleKeyDown);

			return () => {
				document.removeEventListener("keydown", handleKeyDown);
			};
		}
	}, [isOpen]);

	useEffect(() => {
		if (skipFirstPersist()) return;

		// Always persist pending task when not in edit mode (i.e., when bundle is not provided)
		if (!bundle && isOpen) {
			if (pendingTasks.length === 0) {
				addNewTask();
				return;
			}
			persistPendingTasks(LocalStorageKeys.MOVEMENT_TASKS, session?.user_id, JSON.stringify(pendingTasks));
		}

		// If there are pending task and no active task, set the first task as active
		// This ensures that there is always an active task to work with
		// Empty tasks get today's date when activated if their date is stale
		if (pendingTasks.length > 0 && !activeTask) {
			setPendingTasks((prevTasks) =>
				mapTasksWithActivation(prevTasks, (_task, index) => index === 0)
			);
		}
	}, [pendingTasks]);

	useEffect(() => {
		if (activeTask) {
			if (!renderChildren) setRenderChildren(true);

			// Lazy fetch data if in edit mode and only one pending task exists
			// This is to ensure that the data is fetched only when there is a single task to edit
			// This is because calling it immediately after addNewTask might fetch when there are no tasks yet
			// because setting state is asynchronous
			if (prevTaskIdRef.current !== activeTask.id) {
				if (bundle) fetchData();
				prevTaskIdRef.current = activeTask.id;
			}

			// If in edit mode, calculate the task hash
			// This is used to determine if the task details have changed
			// and if the update can proceed
			if (bundle && activeTask && bundleItems.length > 0 && !taskHash) {
				setTaskHash(
					Util.hashObject(
						JSON.stringify(bundleDetails)
						+
						JSON.stringify(bundleItems)
					)
				);
			}
		} else {
			prevTaskIdRef.current = null;
		}
	}, [activeTask]);

	const cleanup = () => {
		setPendingTasks([]);
		setFetchedTask(null);
		setItems([]);
		setTaskHash(null);
		if (changeRef.current) {
			onSuccess?.();
		}
		// setRenderChildren(false);
	};

	const toggleDrawer = (newOpen: boolean) => () => {
		setTasksOpen(newOpen);
	};

	const onDeleteClicked = (uniqueId: string) => {
		updateActiveTask(setPendingTasks, prev => ({
			items: prev.items
				? prev.items.filter(item => item.uniqueId !== uniqueId)
				: [],
		}));
	};

	const columns: GridColDef<Item>[] = [
		{
			field: 'rowNumber',
			headerName: '#',
			width: 20,
			valueGetter: (_, row) => apiRef.current!.getRowIndexRelativeToVisibleRows(row.uniqueId) + 1,
		},
		...(Util.isBoolSettingEnabled(appSettings?.UI_SHOW_ITEM_CODE?.value)
			? [
				{
				field: 'item_code',
				headerName: t('common:tables.itemCode'),
				},
			]
			: []),
		{
			field: 'item',
			headerName: t('common:tables.item'),
			flex: 2,
			minWidth: 250,
		},
		...(Util.isBoolSettingEnabled(appSettings?.UI_SHOW_CATEGORY?.value)
			? [
				{
				field: 'category',
				headerName: t('common:tables.category'),
				},
			]
			: []),
		{
			field: 'quantity',
			headerName: t('common:tables.quantity'),
			type: 'number',
			editable: true
		},
		{
			field: 'price',
			headerName: t('common:tables.amount'),
			type: 'number',
			editable: true
		},
		{
			field: 'total',
			headerName: t('common:tables.lineTotal'),
			flex: 1,
			minWidth: 150,
			maxWidth: 150,
			type: 'number',
			description: t('common:tables.total'),
		},
		{
			field: 'actions',
			headerName: '',
			sortable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams<Item>) => (
				<Tooltip title={t('common:ui.tooltips.delete')}>
					<IconButton color="error" onClick={() => { onDeleteClicked(params.row.uniqueId) }}>
						<RemoveCircleOutline />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return (
		<>
			<MoleDialog
				open={isOpen}
				onClose={toggle}
				maxWidth="xl"
				fullWidth
				themeColor={themeColor}
				paperOptions={{ maxWidth: "95%", width: "95%", maxHeight: "95vh" }}
				disableEscapeKeyDown
				onTransitionExited={cleanup}
			>
				<MoleDialogHeader
					title={t("common:movement.modal.title")}
					subtitle={`${t("common:report.grandTotal")}: ${Util.formatCurrency(activeTask?.total() || 0, currency)}`}
					icon={<SwapHorizOutlined />}
					onClose={toggle}
					themeColor={themeColor}
					extra={
						!bundle ? (
							<Button
								variant="outlined"
								size="small"
								startIcon={<PendingActionsIcon />}
								onClick={() => toggleDrawer(true)()}
							>
								{t("common:ui.buttons.tasks")}
							</Button>
						) : undefined
					}
				/>
				<DialogContent>
					<Paper elevation={24}>
						{renderChildren &&
							<MoveDetail
								setValidateCheck={setValidateDetail}
							/>
						}
						<Divider orientation="vertical" variant="middle" flexItem />
						{renderChildren &&
							< MoveItemDetail
								bundle={fetchedTask?.records}
							/>
						}
					</Paper>
					{renderChildren &&
						<ItemTable<Item, MovementTask<Item>>
							activeTask={activeTask}
							setPendingTasks={setPendingTasks}
							columns={columns}
							itemConstructor={Item}
							bundle={fetchedTask?.records}
							items={items}
							lineNameMode="strict"
							apiRef={apiRef}
						/>
					}
				</DialogContent>
				<MoleDialogActions sx={{ justifyContent: "stretch" }}>
					<Grid container spacing={1} alignItems="center" width="100%">
						<Grid sx={{ flexGrow: 1, minWidth: 0 }}>
							<TextField
								id="details"
								size="small"
								label={t("common:ui.labels.notes")}
								variant="filled"
								fullWidth
								type="text"
								value={activeTask?.description || ""}
								inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
								onChange={(e) => updateActiveTask(setPendingTasks, { description: e.target.value })}
								disabled={!activeTask}
							/>
						</Grid>
						<Grid
							sx={{
								width: { xs: "100%", sm: "100%", md: "auto" },
								display: "flex",
								justifyContent: { xs: "space-between", md: "flex-end" },
								alignItems: "center",
								gap: 1,
							}}
						>
							<Button
								ref={validateBtnRef}
								variant="contained"
								color="success"
								onClick={onSubmit}
								disabled={!canValidate}
								startIcon={<Save />}
							>
								{bundle ? t("common:ui.buttons.amendTransfer") : t("common:ui.buttons.transfer")}
							</Button>
							<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
						</Grid>
					</Grid>
				</MoleDialogActions>
				<TaskPane<Item, MovementTask<Item>>
					tasks={pendingTasks}
					setTasks={setPendingTasks}
					addNewTask={addNewTask}
					tasksOpen={tasksOpen}
					onCallback={() => setTasksOpen(false)}
					currentUserId={userId}
					onTogglePublic={(task) => { void setPublic(task, !task.is_public); }}
					onDeleteTask={(id: string) => deleteTask(id)}
				/>
			</MoleDialog>

			{/* Confirmation Modal: no conditional rendering for perfomance as it is lightweight */
				< ConfirmDialog
					onConfirm={handleConfirm}
					isOpen={confirmModalOpen}
					setIsOpen={setConfirmModalOpen}
					title={t("common:dialog.transferConfirmation")}
					confirmBtnColor="success" />
			}
		</>
	);
};

export default MovementModal;
